import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../../../config/firebase/config';
import { Restaurant } from '../../../types/restaurant';
import { ArrowLeft, Edit2, Save, X, CheckCircle, AlertCircle, Upload, Trash2 } from 'lucide-react';

export default function RestaurantDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Editable fields
  const [editForm, setEditForm] = useState({
    name: '',
    active: true,
    taxRate: 18,
    integrationMode: 'none' as 'api' | 'webhook' | 'polling' | 'none',
  });

  const [processorForm, setProcessorForm] = useState({
    processor: 'none' as 'stripe' | 'cardnet' | 'azul' | 'none',
    // Stripe
    stripePublicKey: '',
    stripeSecretKey: '',
    stripeEnvironment: 'test' as 'test' | 'live',
    // CardNet
    cardnetMerchantId: '',
    cardnetApiSecret: '',
    // Azul
    azulAuth1: '',
    azulAuth2: '',
    azulMerchantId: '',
    azulChannel: 'EC',
  });
  // POS configuration
  const [posForm, setPosForm] = useState({
    provider: 'mock' as 'mock' | 'none',
    enabled: true,
    autoSync: true,
    syncIntervalMinutes: 5,
    apiUrl: '',
    apiKey: '',
    impuestosIncluded: false
  });
  // Logo editing
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showManualModeCleanupConfirm, setShowManualModeCleanupConfirm] = useState(false);

  useEffect(() => {
    loadRestaurant();
  }, [id]);

  const loadRestaurant = async () => {
    if (!id) return;
    
    try {
      const restaurantRef = doc(db, 'restaurants', id);
      const snapshot = await getDoc(restaurantRef);
      
      if (snapshot.exists()) {
        const data = {
          id: snapshot.id,
          ...snapshot.data(),
          createdAt: snapshot.data().createdAt?.toDate() || new Date(),
          updatedAt: snapshot.data().updatedAt?.toDate() || new Date(),
        } as Restaurant;
        
        setRestaurant(data);
        setEditForm({
          name: data.name,
          active: data.active,
          taxRate: data.settings?.taxRate || 18,
          integrationMode: data.integrationMode || 'none',
        });
        // POS config
        setPosForm({
          provider: (data.posConfig?.provider === 'mock' ? 'mock' : 'none') as 'mock' | 'none',
          enabled: data.posConfig?.enabled ?? true,
          autoSync: data.posConfig?.syncSettings?.autoSync ?? true,
          syncIntervalMinutes: data.posConfig?.syncSettings?.syncIntervalMinutes ?? 5,
          apiUrl: data.posConfig?.credentials?.apiUrl || '',
          apiKey: data.posConfig?.credentials?.apiKey || '',
          impuestosIncluded: data.posConfig?.impuestosIncluded ?? false
        });
        
        // Processor config
        setProcessorForm({
          processor: data.processor || 'none',
          stripePublicKey: data.stripeConfig?.publicKey || '',
          stripeSecretKey: '',
          stripeEnvironment: (data.stripeConfig?.environment as 'test' | 'live') || 'test',
          cardnetMerchantId: data.cardnetConfig?.merchantId || '',
          cardnetApiSecret: '',
          azulAuth1: '',
          azulAuth2: '',
          azulMerchantId: data.azulConfig?.merchantId || '',
          azulChannel: data.azulConfig?.channel || 'EC',
        });
        
        // Logo
        setNewLogoUrl(data.logoUrl || '');
        setLogoPreview(data.logoUrl || null);
      }
    } catch (error) {
      console.error('Error cargando restaurante:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (opts?: { confirmedManualModeCleanup?: boolean }) => {
    if (!id || !restaurant) return;

    const previousMode = restaurant.integrationMode || 'none';
    const nextMode = editForm.integrationMode || 'none';
    const isSwitchingToManualNone = previousMode !== 'none' && nextMode === 'none';

    // UX safety: confirm before cleaning mesas when switching to manual mode
    if (isSwitchingToManualNone && !opts?.confirmedManualModeCleanup) {
      setShowManualModeCleanupConfirm(true);
      return;
    }
    
    setSaving(true);
    setMessage(null);
    
    try {
      // Validate Mock POS fields if selected
      if (posForm.provider === 'mock') {
        if (!posForm.apiUrl) {
          throw new Error('Mock POS API URL es requerido');
        }
        if (!posForm.apiKey) {
          throw new Error('Mock POS API Key es requerido');
        }
        if (!posForm.apiUrl.startsWith('http://') && !posForm.apiUrl.startsWith('https://')) {
          throw new Error('API URL debe comenzar con http:// o https://');
        }
      }

      const functions = getFunctions();
      
      // 1. Create new secret if processor credentials changed
      let secretName: string | undefined;
      
      if (processorForm.processor === 'stripe' && processorForm.stripeSecretKey) {
        const createSecret = httpsCallable(functions, 'createProcessorSecret');
        const result = await createSecret({
          restaurantId: id,
          processor: 'stripe',
          credentials: { secretKey: processorForm.stripeSecretKey }
        });
        secretName = (result.data as any).secretName;
      } else if (processorForm.processor === 'cardnet' && processorForm.cardnetApiSecret) {
        const createSecret = httpsCallable(functions, 'createProcessorSecret');
        const result = await createSecret({
          restaurantId: id,
          processor: 'cardnet',
          credentials: { apiSecret: processorForm.cardnetApiSecret }
        });
        secretName = (result.data as any).secretName;
      } else if (processorForm.processor === 'azul' && processorForm.azulAuth1 && processorForm.azulAuth2) {
        const createSecret = httpsCallable(functions, 'createProcessorSecret');
        const result = await createSecret({
          restaurantId: id,
          processor: 'azul',
          credentials: { auth1: processorForm.azulAuth1, auth2: processorForm.azulAuth2 }
        });
        secretName = (result.data as any).secretName;
      }
      
      // 2. Build update object with ALL changes
      const updates: any = {
        // Basic info
        name: editForm.name,
        active: editForm.active,
        'settings.taxRate': editForm.taxRate,
        
        // POS Integration Mode (Super Admin Only)
        integrationMode: editForm.integrationMode,
        
        // POS config (full object so impuestosIncluded and all fields are written)
        posConfig: {
          ...(restaurant?.posConfig && typeof restaurant.posConfig === 'object' ? restaurant.posConfig : {}),
          provider: posForm.provider,
          enabled: posForm.enabled,
          impuestosIncluded: !!posForm.impuestosIncluded,
          syncSettings: {
            ...(restaurant?.posConfig?.syncSettings && typeof restaurant.posConfig.syncSettings === 'object' ? restaurant.posConfig.syncSettings : {}),
            autoSync: posForm.autoSync,
            syncIntervalMinutes: posForm.syncIntervalMinutes ?? 5
          },
          credentials: posForm.provider === 'mock'
            ? { apiUrl: posForm.apiUrl || '', apiKey: posForm.apiKey || '' }
            : (restaurant?.posConfig?.credentials ?? undefined)
        },
        
        // Processor
        processor: processorForm.processor,
        
        // Logo (if changed)
        logoUrl: newLogoUrl || null,
        
        updatedAt: serverTimestamp()
      };
      
      // 3. Add processor-specific config
      if (processorForm.processor === 'stripe') {
        updates.stripeConfig = {
          enabled: true,
          publicKey: processorForm.stripePublicKey,
          environment: processorForm.stripeEnvironment,
          merchantId: '',
          ...(secretName && { secretName }),
          ...(!secretName && restaurant.stripeConfig?.secretName && { secretName: restaurant.stripeConfig.secretName })
        };
        updates.cardnetConfig = null;
        updates.azulConfig = null;
      } else if (processorForm.processor === 'cardnet') {
        updates.cardnetConfig = {
          enabled: true,
          merchantId: processorForm.cardnetMerchantId,
          environment: 'sandbox',
          ...(secretName && { secretName }),
          ...(!secretName && restaurant.cardnetConfig?.secretName && { secretName: restaurant.cardnetConfig.secretName })
        };
        updates.stripeConfig = null;
        updates.azulConfig = null;
      } else if (processorForm.processor === 'azul') {
        updates.azulConfig = {
          enabled: true,
          merchantId: processorForm.azulMerchantId,
          channel: processorForm.azulChannel,
          environment: 'sandbox',
          ...(secretName && { secretName }),
          ...(!secretName && restaurant.azulConfig?.secretName && { secretName: restaurant.azulConfig.secretName })
        };
        updates.stripeConfig = null;
        updates.cardnetConfig = null;
      } else {
        updates.stripeConfig = null;
        updates.cardnetConfig = null;
        updates.azulConfig = null;
      }
      
      // 4. Update Firestore
      const restaurantRef = doc(db, 'restaurants', id);
      await updateDoc(restaurantRef, updates);

      // 5. One-time cleanup when switching to manual mode
      if (isSwitchingToManualNone) {
        console.log('⚠️ Switching to manual mode — cleaning mesas', { restaurantId: id });
        const cleanupFn = httpsCallable(functions, 'cleanAllMesasForManualMode');
        await cleanupFn({ restaurantId: id });
        console.log('✅ Mesas cleaned for manual mode', { restaurantId: id });
      }
      
      setMessage({ type: 'success', text: 'Restaurante actualizado exitosamente' });
      setEditing(false);
      await loadRestaurant();
    } catch (error: any) {
      console.error('Error actualizando restaurante:', error);
      setMessage({ type: 'error', text: error.message || 'Error al actualizar el restaurante' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    
    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'El archivo es demasiado grande (máximo 5MB)' });
      return;
    }
    
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'El archivo debe ser una imagen' });
      return;
    }
    
    setUploadingLogo(true);
    
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `logos/${id}/${Date.now()}_${file.name}`);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      setNewLogoUrl(downloadUrl);
      setLogoPreview(downloadUrl);
      setMessage({ type: 'success', text: 'Imagen subida correctamente. Guarda los cambios para aplicar.' });
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: 'Error al subir la imagen' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = () => {
    if (!logoPreview && !newLogoUrl) return;
    
    setNewLogoUrl('');
    setLogoPreview(null);
    setMessage({ type: 'success', text: 'Logo eliminado. Guarda los cambios para aplicar.' });
  };

  const handleDeleteRestaurant = async () => {
    if (!id) return;
    
    setSaving(true);
    
    try {
      await deleteDoc(doc(db, 'restaurants', id));
      navigate('/super-admin');
    } catch (error: any) {
      console.error('Error deleting restaurant:', error);
      setMessage({ type: 'error', text: 'Error al eliminar el restaurante' });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando detalles...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Restaurante no encontrado</h2>
          <Link
            to="/super-admin"
            className="text-blue-600 hover:text-blue-700"
          >
            ← Volver al Panel
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate('/super-admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Volver al Panel
        </button>
        
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            disabled={saving || uploadingLogo}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Edit2 size={18} />
            Editar
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={18} />
              Cancelar
            </button>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        )}
      </div>

      {/* Success/Error Message */}
      {message && (
        <div className={`mb-6 rounded-lg p-4 flex items-start gap-3 ${
          message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
          ) : (
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          )}
          <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
            {message.text}
          </p>
        </div>
      )}

      {/* Restaurant Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header with Logo */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
          <div className="flex items-center gap-6">
            {/* Logo */}
            {editing ? (
              <div className="w-32 space-y-2">
                {/* Preview */}
                <div className="w-32 h-32 rounded-full bg-white p-2 flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold text-white">
                      {restaurant?.name.charAt(0)}
                    </div>
                  )}
                </div>
                
                {/* Upload button */}
                <label className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg cursor-pointer transition text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed">
                  <Upload size={16} />
                  {uploadingLogo ? 'Subiendo...' : logoPreview ? 'Cambiar' : 'Subir'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={saving || uploadingLogo}
                    className="hidden"
                  />
                </label>
                
                {/* Delete button */}
                {(logoPreview || restaurant?.logoUrl) && (
                  <button
                    onClick={handleDeleteLogo}
                    disabled={saving || uploadingLogo}
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-red-600/80 hover:bg-red-700/80 rounded-lg text-sm text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </button>
                )}
              </div>
            ) : (
              <div className="w-24 h-24">
                {restaurant?.logoUrl ? (
                  <img
                    src={restaurant.logoUrl}
                    alt={restaurant.name}
                    className="w-24 h-24 rounded-full bg-white p-2 object-contain"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold">
                    {restaurant?.name.charAt(0)}
                  </div>
                )}
              </div>
            )}
            
            {/* Resto del header... */}
            <div className="flex-1">
              {editing ? (
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  disabled={saving}
                  className="text-3xl font-bold bg-white/20 border border-white/30 rounded-lg px-4 py-2 w-full disabled:opacity-50"
                />
              ) : (
                <h1 className="text-3xl font-bold">{restaurant?.name}</h1>
              )}
              <p className="text-blue-100 mt-2">ID: {restaurant?.id}</p>
            </div>
            
            <div>
              {editing ? (
                <label className="flex items-center gap-2 cursor-pointer bg-white/20 px-4 py-2 rounded-lg">
                  <input
                    type="checkbox"
                    checked={editForm.active}
                    onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                    disabled={saving}
                    className="w-5 h-5"
                  />
                  <span>Activo</span>
                </label>
              ) : (
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  restaurant?.active
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {restaurant?.active ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="p-8 grid grid-cols-2 gap-8">
          {/* Payment Processor */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Procesador de Pagos</h3>
            
            {editing ? (
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                {/* Processor Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Procesador
                  </label>
                  <select
                    value={processorForm.processor}
                    onChange={(e) => setProcessorForm({ ...processorForm, processor: e.target.value as any })}
                    disabled={saving}
                    className="w-full px-3 py-2 border rounded-lg disabled:opacity-50"
                  >
                    <option value="none">Sin configurar</option>
                    <option value="stripe">Stripe</option>
                    <option value="cardnet">CardNet</option>
                    <option value="azul">Azul</option>
                  </select>
                </div>

                {/* Stripe Fields */}
                {processorForm.processor === 'stripe' && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Public Key"
                      value={processorForm.stripePublicKey}
                      onChange={(e) => setProcessorForm({ ...processorForm, stripePublicKey: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                    <input
                      type="password"
                      placeholder="Secret Key (dejar vacío para mantener)"
                      value={processorForm.stripeSecretKey}
                      onChange={(e) => setProcessorForm({ ...processorForm, stripeSecretKey: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                    <select
                      value={processorForm.stripeEnvironment}
                      onChange={(e) => setProcessorForm({ ...processorForm, stripeEnvironment: e.target.value as 'test' | 'live' })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    >
                      <option value="test">Test</option>
                      <option value="live">Live</option>
                    </select>
                  </div>
                )}

                {/* CardNet Fields */}
                {processorForm.processor === 'cardnet' && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Merchant ID"
                      value={processorForm.cardnetMerchantId}
                      onChange={(e) => setProcessorForm({ ...processorForm, cardnetMerchantId: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                    <input
                      type="password"
                      placeholder="API Secret (dejar vacío para mantener)"
                      value={processorForm.cardnetApiSecret}
                      onChange={(e) => setProcessorForm({ ...processorForm, cardnetApiSecret: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                  </div>
                )}

                {/* Azul Fields */}
                {processorForm.processor === 'azul' && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Merchant ID"
                      value={processorForm.azulMerchantId}
                      onChange={(e) => setProcessorForm({ ...processorForm, azulMerchantId: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                    <input
                      type="text"
                      placeholder="Canal"
                      value={processorForm.azulChannel}
                      onChange={(e) => setProcessorForm({ ...processorForm, azulChannel: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                    <input
                      type="text"
                      placeholder="Auth1 (dejar vacío para mantener)"
                      value={processorForm.azulAuth1}
                      onChange={(e) => setProcessorForm({ ...processorForm, azulAuth1: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                    <input
                      type="text"
                      placeholder="Auth2 (dejar vacío para mantener)"
                      value={processorForm.azulAuth2}
                      onChange={(e) => setProcessorForm({ ...processorForm, azulAuth2: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border rounded-lg text-sm disabled:opacity-50"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Procesador:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    restaurant.processor === 'stripe' ? 'bg-purple-100 text-purple-700' :
                    restaurant.processor === 'cardnet' ? 'bg-blue-100 text-blue-700' :
                    restaurant.processor === 'azul' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {restaurant.processor === 'none' ? 'Sin configurar' : restaurant.processor.toUpperCase()}
                  </span>
                </div>
                
                {restaurant.processor === 'stripe' && restaurant.stripeConfig && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ambiente:</span>
                      <span className="font-medium">{restaurant.stripeConfig.environment}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Public Key:</span>
                      <span className="font-mono text-xs">{restaurant.stripeConfig.publicKey?.substring(0, 20)}...</span>
                    </div>
                  </>
                )}
                
                {restaurant.processor === 'cardnet' && restaurant.cardnetConfig && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Merchant ID:</span>
                      <span className="font-mono text-xs">{restaurant.cardnetConfig.merchantId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ambiente:</span>
                      <span className="font-medium">{restaurant.cardnetConfig.environment}</span>
                    </div>
                  </>
                )}
                
                {restaurant.processor === 'azul' && restaurant.azulConfig && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Merchant ID:</span>
                      <span className="font-mono text-xs">{restaurant.azulConfig.merchantId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Canal:</span>
                      <span className="font-medium">{restaurant.azulConfig.channel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ambiente:</span>
                      <span className="font-medium">{restaurant.azulConfig.environment}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* POS Integration */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Integración POS</h3>
            
            {editing ? (
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                {/* Integration Mode (Super Admin Only) - MUST be consistent with provider */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    🔑 Modo de Integración (Super Admin)
                  </label>
                  <select
                    value={editForm.integrationMode}
                    onChange={(e) => setEditForm({ ...editForm, integrationMode: e.target.value as any })}
                    disabled={saving || posForm.provider === 'none'}
                    className="w-full px-3 py-2 border rounded-lg disabled:opacity-50 disabled:bg-gray-200 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="api">API - Sincronización en tiempo real</option>
                    <option value="webhook">Webhook - Eventos push del POS</option>
                    <option value="polling">Polling - Verificación periódica</option>
                    <option value="none">Ninguno - Sin integración POS (manual)</option>
                  </select>
                  {posForm.provider === 'none' && (
                    <p className="text-xs text-orange-600 mt-2 font-semibold">
                      ⚠️ Deshabilitado: No hay proveedor POS seleccionado
                    </p>
                  )}
                  <p className="text-xs text-gray-600 mt-2">
                    ⚠️ Las mesas heredan este modo. Solo cambia si sabes lo que haces.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proveedor</label>
                  <select
                    value={posForm.provider}
                    onChange={(e) => {
                      const newProvider = e.target.value as 'mock' | 'none';
                      setPosForm({ ...posForm, provider: newProvider });
                      // CRITICAL: If setting provider to "none", force integrationMode to "none"
                      if (newProvider === 'none') {
                        setEditForm({ ...editForm, integrationMode: 'none' });
                      }
                    }}
                    disabled={saving}
                    className="w-full px-3 py-2 border rounded-lg disabled:opacity-50"
                  >
                    <option value="mock">Mock POS</option>
                    <option value="none">Sin POS</option>
                  </select>
                  {posForm.provider === 'none' && (
                    <p className="text-xs text-red-600 mt-2 font-semibold">
                      ⚠️ Sin proveedor = Sin integración (modo manual automático)
                    </p>
                  )}
                </div>
                
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={posForm.enabled}
                    onChange={(e) => setPosForm({ ...posForm, enabled: e.target.checked })}
                    disabled={saving}
                    className="w-4 h-4 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700">POS Habilitado</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={posForm.impuestosIncluded}
                    onChange={(e) => setPosForm({ ...posForm, impuestosIncluded: e.target.checked })}
                    disabled={saving}
                    className="w-4 h-4 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700">POS trae totales con impuestos incluidos</span>
                </label>
                <p className="text-xs text-gray-500 -mt-1">
                  Si está activo, el total del POS ya incluye impuestos. Si no, YAP calculará total con impuesto (subtotal + {restaurant?.settings?.taxRate ?? 18}%).
                </p>
                
                {posForm.provider === 'mock' && (
                  <>
                    {/* API URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mock POS API URL *
                      </label>
                      <input
                        type="text"
                        value={posForm.apiUrl}
                        onChange={(e) => setPosForm({ ...posForm, apiUrl: e.target.value })}
                        placeholder="http://10.0.0.252:3001"
                        disabled={saving}
                        className="w-full px-3 py-2 border rounded-lg disabled:opacity-50 focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        URL base de tu Mock POS API (incluye http:// o https://)
                      </p>
                    </div>

                    {/* API Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Key *
                      </label>
                      <input
                        type="password"
                        value={posForm.apiKey}
                        onChange={(e) => setPosForm({ ...posForm, apiKey: e.target.value })}
                        placeholder="mock_pos_key_restaurant_xyz"
                        disabled={saving}
                        className="w-full px-3 py-2 border rounded-lg disabled:opacity-50 focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        🔒 Clave de autenticación para Mock POS
                      </p>
                    </div>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={posForm.autoSync}
                        onChange={(e) => setPosForm({ ...posForm, autoSync: e.target.checked })}
                        disabled={saving}
                        className="w-4 h-4 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-700">Auto-sincronización</span>
                    </label>
                    
                    {posForm.autoSync && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Intervalo (minutos)
                        </label>
                        <input
                          type="number"
                          value={posForm.syncIntervalMinutes}
                          onChange={(e) => setPosForm({ ...posForm, syncIntervalMinutes: parseInt(e.target.value) || 5 })}
                          disabled={saving}
                          min="1"
                          max="60"
                          className="w-full px-3 py-2 border rounded-lg disabled:opacity-50"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Integration Mode Display */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-bold text-gray-900">🔑 Modo de Integración:</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      restaurant.integrationMode === 'api' ? 'bg-green-100 text-green-700' :
                      restaurant.integrationMode === 'webhook' ? 'bg-purple-100 text-purple-700' :
                      restaurant.integrationMode === 'polling' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {restaurant.integrationMode === 'api' ? 'API' :
                       restaurant.integrationMode === 'webhook' ? 'Webhook' :
                       restaurant.integrationMode === 'polling' ? 'Polling' :
                       'Ninguno (Manual)'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">
                    {restaurant.integrationMode === 'api' ? 'Sincronización en tiempo real' :
                     restaurant.integrationMode === 'webhook' ? 'Eventos push del POS' :
                     restaurant.integrationMode === 'polling' ? 'Verificación periódica' :
                     'Sin integración POS - modo manual'}
                  </p>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Proveedor:</span>
                  <span className="font-medium">{restaurant.posConfig?.provider || 'No configurado'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Estado:</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    restaurant.posConfig?.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {restaurant.posConfig?.enabled ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                {restaurant.posConfig?.provider === 'mock' && restaurant.posConfig?.credentials && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">API URL:</span>
                      <span className="font-medium text-sm break-all">
                        {restaurant.posConfig.credentials.apiUrl || 'No configurado'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">API Key:</span>
                      <span className="font-medium text-sm">
                        {restaurant.posConfig.credentials.apiKey ? '••••••••••••' : 'No configurado'}
                      </span>
                    </div>
                  </>
                )}
                {restaurant.posConfig?.syncSettings && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Auto-sync:</span>
                      <span className="font-medium">{restaurant.posConfig.syncSettings.autoSync ? 'Sí' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Intervalo:</span>
                      <span className="font-medium">{restaurant.posConfig.syncSettings.syncIntervalMinutes} min</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Totales con impuestos incluidos:</span>
                  <span className="font-medium">{restaurant.posConfig?.impuestosIncluded ? 'Sí' : 'No'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Settings */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Configuración</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tasa de Impuesto:</span>
                {editing ? (
                  <input
                    type="number"
                    value={editForm.taxRate}
                    onChange={(e) => setEditForm({ ...editForm, taxRate: parseFloat(e.target.value) })}
                    className="w-24 px-3 py-1 border rounded"
                    step="0.1"
                    min="0"
                    max="100"
                  />
                ) : (
                  <span className="font-medium">{restaurant.settings?.taxRate}%</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Moneda:</span>
                <span className="font-medium">{restaurant.settings?.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Dividir Cuenta:</span>
                <span className="font-medium">{restaurant.settings?.allowSplitBill ? 'Sí' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Propinas:</span>
                <span className="font-medium">{restaurant.settings?.allowTipping ? 'Sí' : 'No'}</span>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Información del Sistema</h3>
            <div className="space-y-3">
              <div>
                <span className="text-gray-600 block text-sm">Creado:</span>
                <span className="font-medium">{restaurant.createdAt?.toLocaleString('es-ES')}</span>
              </div>
              <div>
                <span className="text-gray-600 block text-sm">Última Actualización:</span>
                <span className="font-medium">{restaurant.updatedAt?.toLocaleString('es-ES')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>


    {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        <Link
          to={`/admin`}
          className={`bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 transition text-center ${
            (editing || saving || uploadingLogo) 
              ? 'pointer-events-none opacity-50' 
              : ''
          }`}
        >
          <div className="text-2xl mb-2">👤</div>
          <div className="font-semibold text-gray-900">Ver como Admin</div>
          <div className="text-xs text-gray-500 mt-1">Ver panel del restaurante</div>
        </Link>

        <Link
          to={`/super-admin/restaurants/${id}/statistics`}
          className={`bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 transition text-center ${
            (editing || saving || uploadingLogo) 
              ? 'pointer-events-none opacity-50' 
              : ''
          }`}
        >
          <div className="text-2xl mb-2">📊</div>
          <div className="font-semibold text-gray-900">Estadísticas</div>
          <div className="text-xs text-gray-500 mt-1">Estadísticas de rendimiento</div>
        </Link>

        <Link
          to={`/super-admin/restaurants/${id}/transactions`}
          className={`bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 transition text-center ${
            (editing || saving || uploadingLogo) 
              ? 'pointer-events-none opacity-50' 
              : ''
          }`}
        >
          <div className="text-2xl mb-2">💳</div>
          <div className="font-semibold text-gray-900">Transacciones</div>
          <div className="text-xs text-gray-500 mt-1">Historial de transacciones</div>
        </Link>
      </div>

      {/* Danger Zone */}
      <div className="mt-8 bg-red-50 border-2 border-red-200 rounded-xl p-6">
        <h2 className="text-lg font-bold text-red-900 mb-2">Cuidado</h2>
        <p className="text-red-700 mb-4">
          Una vez que elimines este restaurante, no hay vuelta atrás. Esto eliminará permanentemente todos los datos asociados.
        </p>
        
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
          >
            Eliminar Restaurante
          </button>
        ) : (
          <div className="bg-white border border-red-300 rounded-lg p-4">
            <p className="font-semibold text-red-900 mb-4">
              ¿Estás completamente seguro? Escribe "{restaurant.name}" para confirmar:
            </p>
            <input
              type="text"
              placeholder={restaurant.name}
              className="w-full px-4 py-2 border-2 border-red-300 rounded-lg mb-4"
              id="delete-confirm-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  const input = (document.getElementById('delete-confirm-input') as HTMLInputElement);
                  if (input?.value === restaurant.name) {
                    await handleDeleteRestaurant();
                  } else {
                    alert('El nombre no coincide');
                  }
                }}
                disabled={saving}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                {saving ? 'Eliminando...' : 'Sí, eliminar permanentemente'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Manual mode cleanup confirmation modal */}
      {showManualModeCleanupConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Cambiar a modo manual</h2>
            <p className="text-sm text-gray-700 mb-4">
              Cambiar a modo manual eliminará la sincronización con el POS y limpiará los datos actuales de mesas.
              ¿Deseas continuar?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowManualModeCleanupConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  setShowManualModeCleanupConfirm(false);
                  await handleSave({ confirmedManualModeCleanup: true });
                }}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {saving ? 'Confirmando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}